/**
 * The first message from somebody you do not know.
 *
 * It used to arrive in the same list as your friends, which is how a games app
 * becomes a place people get shouted at. These wait here instead, with the
 * whole message visible - deciding without reading it is not a decision - and
 * three ways out: answer it, which makes it an ordinary conversation, ignore
 * it, or block them.
 *
 * Nothing is hidden from you and nothing is deleted. The only thing that
 * changes is which list it sits in until you have looked.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { ago } from '@/lib/notifications';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { EmojiText } from './EmojiText';
import { UserName } from './user';
import { Button, useToast } from './ui';

type Request = { user: PublicUser; at: number; preview: string; messageId: string };

const IGNORED_KEY = 'pogu.dm.ignored';

function readIgnored(): string[] {
  try { return JSON.parse(localStorage.getItem(IGNORED_KEY) || '[]'); } catch { return []; }
}

export function useMessageRequests() {
  const me = useSession((s) => s.me);
  return useQuery({
    queryKey: ['dm-requests'],
    queryFn: () => api.get<{ requests: Request[] }>('/api/dms/requests'),
    enabled: !!me,
    staleTime: 30_000
  });
}

/** How many are waiting, for a badge. */
export function useRequestCount(): number {
  const q = useMessageRequests();
  const ignored = readIgnored();
  return (q.data?.requests ?? []).filter((r) => !ignored.includes(r.user.id)).length;
}

export function MessageRequests({ compact }: { compact?: boolean }) {
  const q = useMessageRequests();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [ignored, setIgnored] = useState<string[]>(() => readIgnored());
  const [open, setOpen] = useState(false);

  const block = useMutation({
    mutationFn: (userId: string) => api.post('/api/friends/block', { userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dm-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['friends'] });
      toast('Blocked. They cannot message you again.', 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  function ignore(userId: string) {
    const next = [...ignored, userId];
    setIgnored(next);
    try { localStorage.setItem(IGNORED_KEY, JSON.stringify(next.slice(-200))); } catch { /* storage off */ }
  }

  const list = (q.data?.requests ?? []).filter((r) => !ignored.includes(r.user.id));
  if (!list.length) return null;

  return (
    <section
      aria-labelledby="dm-requests-title"
      className={cn(
        'pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]',
        compact ? 'p-2.5' : 'p-3'
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left outline-none"
      >
        <Icon name="inbox" size={15} className="flex-none text-accent" />
        <h3 id="dm-requests-title" className="m-0 flex-1 text-[13.5px] font-bold text-ink">Message requests</h3>
        <span className="grid h-5 min-w-5 place-items-center rounded-full bg-accent px-1.5 text-[11.5px] font-bold text-accent-fg tabular-nums">
          {list.length}
        </span>
        <Icon name="chevronD" size={14} className={cn('flex-none text-ghost transition-transform', open && 'rotate-180')} />
      </button>

      {open ? (
        <ul className="m-0 mt-2.5 flex list-none flex-col gap-2 p-0">
          {list.slice(0, 8).map((r) => (
            <li key={r.user.id} className="rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-2.5">
              <div className="flex items-center gap-2.5">
                <Avatar user={r.user} size={30} presence />
                <span className="min-w-0 flex-1">
                  <UserName user={r.user} className="block truncate text-[13px] font-semibold" />
                  <span className="block truncate text-[11.5px] text-mute">@{r.user.username} · {ago(r.at)}</span>
                </span>
              </div>
              {r.preview ? (
                <p className="m-0 mt-1.5 line-clamp-3 text-[12.5px] text-dim">
                  <EmojiText text={r.preview} size={15} />
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button size="sm" kind="primary" onClick={() => go('chat/' + r.user.username)}>Read and reply</Button>
                <Button size="sm" kind="outline" onClick={() => ignore(r.user.id)}>Ignore</Button>
                <Button size="sm" kind="plain" onClick={() => block.mutate(r.user.id)}>Block</Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 mt-1.5 text-[12px] text-mute">
          {list.length === 1 ? 'One person you have not spoken to before has messaged you.' : list.length + ' people you have not spoken to before have messaged you.'}
        </p>
      )}
    </section>
  );
}
